import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lead, LeadStatus } from '@/types/lead';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Trash2, ChevronDown, ChevronUp, FileText, Phone, Mail, Globe, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import LeadStatusSelector from './LeadStatusSelector';
import { useAuth } from '@/context/AuthContext';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';

interface ListViewProps {
  leads: Lead[];
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
}

const ListView: React.FC<ListViewProps> = ({
  leads,
  onToggleFavorite,
  onDelete,
}) => {
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleRowClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };
  
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {leads.map((lead) => (
        <Collapsible
          key={lead.id}
          open={openItems[lead.id]}
          onOpenChange={() => toggleItem(lead.id)}
        >
          <div className="bg-background border border-border/60 rounded-xl overflow-hidden hover:shadow-md transition-all duration-300 elevation-lifted hover:elevation-floating">
            <div 
              className="flex items-start justify-between p-5 flex-wrap gap-4 hover:bg-muted/40 transition-colors cursor-pointer"
              onClick={() => handleRowClick(lead.id)}
            >
              <div className="flex flex-grow min-w-0 gap-4">
                {lead.imageUrl && (
                  <div className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 border-2 border-border/20 hover:scale-105 transition-transform">
                    <img
                      src={lead.imageUrl}
                      alt={lead.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              
                <div className="flex-grow min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-lg tracking-tight truncate">{lead.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground/80 truncate mb-3">{lead.address}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {lead.category && (
                      <Badge variant="outline" className="text-xs border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
                        {lead.category}
                      </Badge>
                    )}
                    {lead.rating && (
                      <Badge variant="secondary" className="text-xs bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
                        <Star className="h-3 w-3 mr-1 fill-yellow-500 text-yellow-500" />
                        {lead.rating}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                    {lead.phone && (
                      <div className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                        <Phone className="h-3.5 w-3.5 text-primary/70" />
                        <a href={`tel:${lead.phone}`} className="hover:text-primary transition-colors hover:underline underline-offset-2">
                          {lead.phone}
                        </a>
                      </div>
                    )}
                    {lead.email && (
                      <div className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                        <Mail className="h-3.5 w-3.5 text-primary/70" />
                        <a href={`mailto:${getPrimaryEmail(lead.email)}`} className="hover:text-primary transition-colors truncate max-w-[150px] hover:underline underline-offset-2">
                          {formatEmailForDisplay(lead.email)}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            
              <div className="flex flex-col gap-3 items-end ml-auto">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(lead.id);
                    }}
                    className="text-muted-foreground hover:text-yellow-500 transition-all hover:scale-110"
                    aria-label="Toggle favorite"
                  >
                    <Star className={lead.isFavorite ? "h-5 w-5 fill-yellow-500 text-yellow-500" : "h-5 w-5"} />
                  </button>
                  
                  {lead.createdBy === user?.id && (
                    <DeleteConfirmationDialog 
                      entityName={lead.name}
                      onConfirm={() => onDelete(lead.id)}
                      trigger={
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="text-muted-foreground hover:text-destructive transition-all hover:scale-110"
                          aria-label="Delete lead"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      }
                    />
                  )}
                </div>
                
                <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="w-24 hover:bg-primary/5">
                    {openItems[lead.id] ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1 transition-transform" />
                        Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1 transition-transform" />
                        More
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          
            <CollapsibleContent>
              <div className="p-6 bg-gradient-to-br from-muted/20 to-muted/5 border-t border-border/40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Contact Information</h4>
                  <div className="text-sm space-y-1">
                    {lead.phone && (
                      <p className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{lead.phone}</span>
                      </p>
                    )}
                    {lead.email && (
                      <p className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                        <a href={`mailto:${getPrimaryEmail(lead.email)}`} className="text-primary hover:underline break-all">
                          {formatEmailForDisplay(lead.email)}
                        </a>
                      </p>
                    )}
                    {lead.website && (
                      <p className="flex items-center">
                        <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                        <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                          {lead.website}
                        </a>
                      </p>
                    )}
                    {lead.rating && (
                      <p><span className="font-medium">Rating:</span> {lead.rating} ★ ({lead.reviewCount} reviews)</p>
                    )}
                    <p><span className="font-medium">Added:</span> {format(new Date(lead.createdAt), 'MMM d, yyyy')}</p>
                  </div>
                </div>
                
                <div>
                  {lead.notes.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary/70" />
                        Notes
                      </h4>
                      <ul className="text-sm space-y-2">
                        {lead.notes.map((note) => (
                          <li key={note.id} className="p-3 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/10">
                            <p>{note.content}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(note.createdAt), 'MMM d, yyyy')}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {lead.documents.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Documents</h4>
                      <ul className="text-sm space-y-1">
                        {lead.documents.map((doc) => (
                          <li key={doc.id} className="flex items-center">
                            <FileText className="h-3 w-3 mr-1" />
                            <a href={doc.url} className="text-primary hover:underline break-all" target="_blank" rel="noopener noreferrer">
                              {doc.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleContent>
          </div>
        </Collapsible>
      ))}
    </div>
  );
};

export default ListView;
